import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { isDefined, Maybe } from 'dg-web-shared/lib/MaybeV2.ts';
import { localeCompare } from 'dg-web-shared/lib/String.ts';
import * as Text from '../../../common/i18n/Text.ts';
import * as OperatorLoginsState from '../../../common/state/OperatorLoginsState.ts';
import { getLoginTitle } from '../../../common/state/OperatorLoginsState.ts';
import { SingleSelection } from '../../../ui/labeled-elements/SingleSelection.tsx';
import {
    NumberMultiSelectionSlideIn,
    Option,
} from '../../../ui/slidein/MultiSelectionSlideIn.tsx';
import {
    clearOperatorLogins,
    setOperatorLogins,
} from '../../actions/FilteredPermitsActions.ts';
import { operatorLoginFilterTexts } from '../../i18n/FilteredPermitsTexts.ts';
import * as FilteredPermitsState from '../../state/FilteredPermitsState.ts';

export interface OperatorLoginFilterTexts {
    filterTitle: Text.Translation;
    onlineUser: Text.Translation;
}

export const operatorLoginSelection = (
    logins: OperatorLoginsState.OperatorLogin[],
    selection: Maybe<number[]>,
) =>
    isDefined(selection) && selection.length > 0
        ? `${selection.length}/${logins.length + 1}`
        : '';

export function OperatorLoginFilterHalfSlideIn() {
    const { storeState, update } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
        operatorLogins: OperatorLoginsState.List.get(store),
    }));
    const { language } = useLanguage();

    function renderOptions(): Option<number>[] {
        const texts = operatorLoginFilterTexts[language];
        return [
            {
                id: FilteredPermitsState.Filter.OperatorLoginFilterOnlineOption,
                displayText: texts.onlineUser(),
                hasSpacingBottom: true,
            } as Option<number>,
        ].concat(
            storeState.operatorLogins.data
                .map(l => {
                    return {
                        id: l.operatorLoginId,
                        displayText: getLoginTitle(l),
                    };
                })
                .sort((a, b) => localeCompare(a.displayText, b.displayText)),
        );
    }

    return (
        <NumberMultiSelectionSlideIn
            open={storeState.filter.operatorLoginSelectionVisible}
            heading={operatorLoginFilterTexts[language].filterTitle()}
            selection={storeState.filter.operatorLogins || []}
            options={renderOptions()}
            onLeftActionClick={() =>
                update(store =>
                    FilteredPermitsState.Filter.stateWrite(store, {
                        operatorLoginSelectionVisible: false,
                    }),
                )
            }
            setNewSelection={v => update(store => setOperatorLogins(store, v))}
        />
    );
}

export function OperatorLoginFilterSelection() {
    const { storeState, update } = useStore(store => ({
        filter: FilteredPermitsState.Filter.get(store),
        operatorLogins: OperatorLoginsState.List.get(store),
    }));
    const { language } = useLanguage();

    const texts = operatorLoginFilterTexts[language];
    const clear = isDefined(storeState.filter.operatorLogins)
        ? () => update(clearOperatorLogins)
        : null;

    return (
        <SingleSelection
            label={texts.filterTitle()}
            focused={false}
            selection={operatorLoginSelection(
                storeState.operatorLogins.data,
                storeState.filter.operatorLogins,
            )}
            onClick={() =>
                update(store =>
                    FilteredPermitsState.Filter.stateWrite(store, {
                        operatorLoginSelectionVisible: true,
                    }),
                )
            }
            onClear={clear}
        />
    );
}
